import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import AuthContext from '../../store/AuthContext';
import { loadAndMountOpenKnots } from './OpenKnotsContainer.helpers';
import { openKnotsCopy } from '../../utils/constants/openKnotsLabel';

/**
 * Container that mounts the Open Knots microfrontend using the preferred `mount` API.
 * This is used when the buyer dashboard tab is set to 'openKnots'.
 */
const LOADING_DELAY_MS = 250; // Don't show loading overlay for fast loads (reduces flicker)

const OpenKnotsContainer = ({ knotTabVisibility: knotTabVisibilityProp }) => {
    const containerRef = useRef(null);
    const unmountRef = useRef(null);
    const navigate = useNavigate();
    const outletContext = useOutletContext();
    const knotTabVisibility = knotTabVisibilityProp ?? outletContext?.knotTabVisibility;
    const ctx = useContext(AuthContext);
    const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
    const [errorMsg, setErrorMsg] = useState('');
    const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
    const baseUrlUser = process.env.REACT_APP_USER_MANAGEMENT;

    useEffect(() => {
        const mountState = { completed: false };
        const timers = { loadTimeout: null, loadingOverlayTimer: null };

        if (ctx.isLoggedIn) {
            loadAndMountOpenKnots({
                containerRef,
                ctx,
                baseUrlUser,
                knotTabVisibility,
                navigate,
                setStatus,
                setErrorMsg,
                setShowLoadingOverlay,
                unmountRef,
                mountState,
                timers,
                loadingDelayMs: LOADING_DELAY_MS,
            });
        }

        return () => {
            mountState.completed = false;
            if (timers.loadTimeout) clearTimeout(timers.loadTimeout);
            if (unmountRef.current) {
                unmountRef.current();
                unmountRef.current = null;
            }
            setStatus('loading');
            setErrorMsg('');
            setShowLoadingOverlay(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when auth/user/navigate change, not full ctx
    }, [
        ctx.isLoggedIn,
        ctx.userData,
        ctx.amplifyAuth,
        ctx.onLogout,
        ctx.handleUserData,
        baseUrlUser,
        navigate,
        knotTabVisibility,
    ]);

    const authReady = Boolean(ctx.isLoggedIn);
    const showLoading = !authReady || status === 'loading' || showLoadingOverlay;

    return (
        <div
            style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                minHeight: '60vh',                
            }}>
            <style>{`
        /* ── CSS Isolation for Open Knots Microfrontend ───────────────────────
         *
         * The child app (129knots-openknots-ui) is built with postcss-prefix-selector
         * which scopes every CSS selector to .openknots-microfrontend-container at
         * build time. The rules below establish this element as the CSS boundary.
         *
         * CSS containment:
         *  - contain: style  → CSS custom properties set on this element (by the
         *                      child's scoped :root rules) do NOT propagate outside
         *                      the container; host tokens stay outside, child tokens
         *                      stay inside
         *
         * NOTE: "contain: layout" and "contain: paint" are intentionally NOT used.
         * Both layout and paint containment make position:fixed children position
         * relative to the container box instead of the viewport. This breaks native
         * Bootstrap modals (BidsReceivedModal, ConfirmationModal) which use
         * position:fixed to overlay the full viewport. Only contain:style is safe
         * here — it scopes CSS custom properties without affecting fixed positioning.
         *
         * NOTE: "all: revert" was removed.
         * It previously reset inherited host styles, but it also cancelled the CSS
         * custom properties that postcss-prefix-selector scopes to this element
         * (by rewriting :root to .openknots-microfrontend-container). Removing it
         * preserves the child's design tokens. Targeted resets below replace it.
         * ─────────────────────────────────────────────────────────────────────── */
        .openknots-microfrontend-container {
          contain: style;
          /* Ensure consistent base font size independent of host app resets */
          font-size: 1rem;
          /* Prevent host box-sizing from affecting child components */
          box-sizing: border-box;
          /* Match host background to avoid white flash before child paints */
          min-height: 100%;
        }
        .openknots-microfrontend-container * {
          box-sizing: border-box;
        }
        /* Child root when embedded – avoid white flash on first paint */
        .openknots-microfrontend-container > div {
          background: transparent;
          min-height: 100%;
        }

        

        /* 1. Column widths — handled by child's postcss-prefixed rule.
         * postcss-prefix-selector gives the child's rule specificity (0,2,1)
         * which beats the host Notifications.css rule (0,1,1 + !important)
         * because for !important ties, higher specificity wins.
         * No reset needed here; adding one would cause source-order conflicts. */

        /* 2. Host sets display:table on tr which corrupts Carbon's row layout */
        .openknots-microfrontend-container .cds--data-table tr {
          display: table-row !important;
        }

        /* 3. Host sets background:none on tbody — skip RFQ grid (uses tableBodyContainer + dashboard tokens) */
        .openknots-microfrontend-container .cds--data-table tbody:not(.tableBodyContainer) {
          background: revert !important;
        }

        /* 4. Host sets header label text to white — restores child's colour */
        .openknots-microfrontend-container .cds--table-header-label {
          color: unset !important;
          font-weight: unset !important;
        }

        /* 5. Host overrides td text colour/weight globally */
        .openknots-microfrontend-container .cds--data-table td {
          color: unset !important;
          font-weight: unset !important;
        }

        /* 6. Host Grid/style.css: white th cells + gray sort buttons under dashboard-bottom-banner */
        .dashboard-bottom-banner .openknots-microfrontend-container .opk-rfq-grid .cds--data-table thead th {
          background-color: #deedff !important;
        }
        .dashboard-bottom-banner .openknots-microfrontend-container .opk-rfq-grid button.cds--table-sort,
        .dashboard-bottom-banner .openknots-microfrontend-container .opk-rfq-grid button.cds--table-sort:hover {
          background: #deedff !important;
          background-color: #deedff !important;
        }
        .dashboard-bottom-banner .openknots-microfrontend-container .opk-rfq-grid .cds--data-table tbody {
          background: rgba(222, 237, 255, 0.58) !important;
          backdrop-filter: blur(34.25px);
          -webkit-backdrop-filter: blur(34.25px);
        }
        .dashboard-bottom-banner .openknots-microfrontend-container .opk-rfq-grid .cds--data-table tbody td,
        .dashboard-bottom-banner .openknots-microfrontend-container .opk-rfq-grid .cds--data-table tbody td.fixedColumn {
          background-color: rgba(222, 237, 255, 0.58) !important;
          box-shadow: none !important;
        }

        /* ── Modal backdrop (native Bootstrap modals rendered in document.body) ── */
        .modal-backdrop { position: fixed; inset: 0; z-index: 1050; background-color: #000; transition: opacity .15s linear; }
        .modal-backdrop.fade { opacity: 0; }
        .modal-backdrop.show { opacity: 0.5; }
        /* Dialog must sit above that body backdrop. Child CSS is scoped to the
         * microfrontend mount, so an in-tree #supplierRespModal was invisible
         * (only the dimmed overlay showed) until the modal was portaled to body. */
        .modal { position: fixed; z-index: 1055; }
        .modal.fade.show { opacity: 1; }

        /* ── Portal-to-body styles ──────────────────────────────────────────────
         *
         * React-Bootstrap Popover, Tooltip, and Modal portal to document.body by
         * default. The child app's postcss-prefixed styles don't reach those nodes
         * since they're outside .openknots-microfrontend-container. These rules
         * duplicate the child's custom styles so they apply globally.
         * ────────────────────────────────────────────────────────────────────── */

        /* Dark popover for Supplier list "+N more" cells */
        .supplier-list-cell-popover {
          --bs-popover-bg: #525252;
          --bs-popover-border-color: #525252;
        }
        .supplier-list-cell-popover .popover-body {
          background-color: #525252;
          color: #fff;
          padding: 0.5rem 0.75rem;
        }
        .supplier-list-cell-popover.bs-popover-auto > .popover-arrow::before {
          border-top-color: #525252;
          border-bottom-color: #525252;
          border-left-color: #525252;
          border-right-color: #525252;
        }

        /* Dark popover for RFQ list cell popovers */
        .openknots-cell-popover {
          --bs-popover-bg: #1f2937;
          --bs-popover-border-color: #374151;
        }
        .openknots-cell-popover .popover-body {
          background-color: #1f2937;
          color: #f3f4f6;
          padding: 0.5rem 0.75rem;
        }
        .openknots-cell-popover.bs-popover-auto > .popover-arrow::before {
          border-top-color: #374151;
          border-bottom-color: #374151;
          border-left-color: #374151;
          border-right-color: #374151;
        }
        .openknots-cell-popover.bs-popover-auto > .popover-arrow::after {
          border-top-color: #1f2937;
          border-bottom-color: #1f2937;
          border-left-color: #1f2937;
          border-right-color: #1f2937;
        }

        /* Delete supplier confirmation modal */
        .supplier-delete-modal .modal-content {
          border-radius: 12px;
          border: none;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
        }
        .supplier-delete-modal__header .btn-close { opacity: 0.5; }
        .supplier-delete-modal__body {
          padding-left: 1.5rem;
          padding-right: 1.5rem;
          padding-bottom: 1rem;
        }
        .supplier-delete-modal__icon-wrap {
          width: 56px;
          height: 56px;
          margin: 0 auto 1rem;
          border-radius: 50%;
          background-color: #e5e5e5;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .supplier-delete-modal__icon { font-size: 1.5rem; color: #737373; line-height: 1; }
        .supplier-delete-modal__message { font-size: 1rem; font-weight: 600; color: #171717; }
        .supplier-delete-modal__footer { padding-top: 0; padding-bottom: 1.25rem; }
        .supplier-delete-modal .modal-footer .supplier-delete-modal__btn-cancel.btn {
          min-width: 7rem;
          padding: 0.5rem 1.25rem;
          border-radius: 8px;
          font-weight: 500;
          background: #f5f5f5;
          border: 1px solid #d4d4d4;
          color: #000;
        }
        .supplier-delete-modal .modal-footer .supplier-delete-modal__btn-cancel.btn:hover:not(:disabled) {
          background: #ebebeb;
          color: #000;
        }
        .supplier-delete-modal__btn-delete {
          min-width: 7rem;
          padding: 0.5rem 1.25rem;
          border-radius: 8px;
          font-weight: 500;
          background: #ef4444;
          border: none;
          color: #fff;
        }
        .supplier-delete-modal__btn-delete:hover:not(:disabled) { background: #dc2626; color: #fff; }
        .supplier-delete-modal__btn-delete:disabled,
        .supplier-delete-modal .modal-footer .supplier-delete-modal__btn-cancel.btn:disabled { opacity: 0.65; }

        /* ── Multiselect overrides ───────────────────────────────────────────────
         *
         * multiselect-react-dropdown and react-multi-select-component both inject
         * CSS globally at runtime (via styleSheet.cssText), bypassing postcss
         * scoping. Override with scoped higher-specificity rules to keep the
         * child's intended hover colours.
         * ──────────────────────────────────────────────────────────────────────*/
        .openknots-microfrontend-container .multiSelectContainer li:hover {
          background-color: #f3f4f6 !important;
          color: #111827 !important;
        }
        .openknots-microfrontend-container .highlightOption,
        .openknots-microfrontend-container .highlightOption:hover {
          background-color: #dbeafe !important;
          color: #1d4ed8 !important;
        }
        .openknots-microfrontend-container .optionListContainer { z-index: 200 !important; }
        .openknots-microfrontend-container .rmsc .dropdown-content { z-index: 200 !important; }
      `}</style>

            {/* The microfrontend must always have a real DOM node to mount into */}
            <div
                ref={containerRef}
                className='openknots-microfrontend-container'
                style={{ width: '100%', height: '100%', minHeight: 200 }}
            />

            {showLoading && (
                <div
                    className='p-4 text-center d-flex align-items-center justify-content-center'
                    style={{
                        position: 'absolute',
                        inset: 0,
                        minHeight: '60vh',
                        background: 'var(--neutral-100, #f5f5f5)',
                        zIndex: 1,
                    }}>
                    {!authReady ? 'Loading...' : openKnotsCopy('Loading {openKnots}...')}
                </div>
            )}

            {status === 'error' && (
                <div className='p-4 text-center text-danger' style={{ position: 'absolute', inset: 0 }}>
                    {errorMsg || openKnotsCopy('Failed to load {openKnots} — remote unavailable')}
                    <div className='mt-3'>
                        <button
                            type='button'
                            className='btn btn-outline-danger btn-sm'
                            onClick={() => {
                                setStatus('loading');
                                setErrorMsg('');
                            }}>
                            Retry
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OpenKnotsContainer;
